import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Effect } from 'effect';
import { API_ORIGIN } from '../lib/host';
import { getClerkTokenEffect, clerkTokenBridgeErrorCause } from './clerkTokenBridge';
import { attemptApi, attemptApiPromise, failApi, runApiPromise } from './effect-runtime';

export const prepareOkraHeadersEffect = Effect.fn('web.okraApi.prepareHeaders')(function* (
  headers: Headers,
) {
  const token = yield* getClerkTokenEffect().pipe(
    Effect.catch((error) =>
      failApi('token', 'okraApi.prepareHeaders', clerkTokenBridgeErrorCause(error) ?? error),
    ),
  );
  return yield* attemptApi('token', 'okraApi.prepareHeaders', () => {
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set('Content-Type', 'application/json');
    return headers;
  });
});

const dashboardBaseQuery = fetchBaseQuery({ baseUrl: API_ORIGIN, credentials: 'omit' });

/**
 * Base RTK Query slice for the okraPDF dashboard.
 *
 * In prod requests go same-origin via the web Worker's AGENT_SESSION
 * binding (skips DNS/TLS/CORS/internet hop). In dev they hit api.okrapdf.com
 * directly — see lib/host.ts for the fallback logic. Clerk JWT is attached
 * via clerkTokenBridge when a session exists.
 *
 * Endpoints are injected per feature in src/api/endpoints/* via
 * `okraApi.injectEndpoints(...)`. This base owns the single reducer, cache,
 * and the full tagTypes namespace so cross-tab cache invalidation stays
 * identical to the former monolithic slice.
 *
 * NOTE: `reducerPath` is intentionally kept as 'tablesApi' so the store
 * wiring and the cache key are unchanged. `tablesApi` is re-exported as an
 * alias of `okraApi` from src/api/tablesApi.ts for backwards compatibility.
 */
export const okraApi = createApi({
  reducerPath: 'tablesApi',
  // prepareHeaders does not receive api.signal. Keep auth in the request's fiber.
  baseQuery: (args, api, extraOptions) =>
    runApiPromise(
      Effect.gen(function* () {
        const request = typeof args === 'string' ? { url: args } : args;
        const headers = yield* attemptApi(
          'token',
          'okraApi.headers',
          () => new Headers(request.headers),
        );
        yield* prepareOkraHeadersEffect(headers);
        return yield* attemptApiPromise('fetch', 'okraApi.baseQuery', () =>
          Promise.resolve(dashboardBaseQuery({ ...request, headers }, api, extraOptions)),
        );
      }),
      { signal: api.signal },
    ).catch((error: unknown) => {
      if (api.signal.aborted)
        return { error: { status: 'FETCH_ERROR' as const, error: 'Request aborted' } };
      throw error;
    }),
  tagTypes: [
    'Tables',
    'TableRows',
    'Agents',
    'SessionTurns',
    'Environments',
    'BillingUsage',
    'ApiKeys',
    'Webhooks',
    'Documents',
    'Files',
    'Collections',
    'HostedPdfs',
    'CustomDomains',
    'Jobs',
    'Workflows',
  ],
  endpoints: () => ({}),
});
